/*
 * Wall Art Collection Filters — client-side filter pipeline for the
 * 5 dropdowns in `snippets/wall-art-collection-filters.liquid`.
 *
 * Owns end-to-end:
 *   1. Fetch taxonomy.json + the per-collection product index (GCS).
 *   2. Populate the 5 dropdowns (Subject / Color / Room / Style / Pieces).
 *   3. Filter the in-memory product list on URL params + checkbox state.
 *   4. Render product cards into #ProductGridContainer (lazy-loaded
 *      via IntersectionObserver, matches Dawn's card markup so CSS is
 *      reused).
 *   5. Update URL via pushState on every change (bookmarkable +
 *      back/forward).
 *
 * Why client-side: Shopify's storefront filter system silently disables
 * `collection.filters` and ?filter.p.* URL params for collections over
 * ~5,000 products (the parent collections — `animals`, `all`, etc.).
 * Sidestepping that entirely by fetching a pre-built product index from
 * GCS gets filtering working at any catalog size.
 *
 * Bug fixes rolled in:
 *   1. Subject menu now recursively renders the FULL descendant tree of
 *      the current collection (or all subject roots when not on a
 *      subject collection).
 *   2. On a child subject collection (e.g. /collections/farm-animals),
 *      Subject dropdown is scoped to that child + its descendants only.
 *   3. Style dropdown: parent checkbox cascades to all descendants.
 *   4. Style/Subject dimension collision fixed via
 *      `taxonomy.nodes_by_dim[dim][handle]` (the flat `nodes` map has
 *      cross-dim handle collisions like `abstract`).
 *   5a. COLOR_HEX populated for all common color handles (combos
 *       included).
 *   5b. Combo color handles (`black_and_blue`) render as a circle split
 *       diagonally — `linear-gradient(135deg, A 50%, B 50%)`.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────
   * Constants
   * ──────────────────────────────────────────────────────────────── */

  const PAGE_SIZE = 36;
  /**
   * Width of the card hero image we request from the resize CDN. A
   * single fixed width (instead of srcset) means our prefetch URLs
   * match the rendered URLs exactly — guaranteed cache hit on paint.
   * 600w looks crisp on retina cards (which display at ~300px wide)
   * and is still small (~15 KB WebP). Below-fold prefetched images
   * use the same width so adjacent pages are fully cache-warm.
   */
  const CARD_IMAGE_WIDTH = 600;
  const TITLE_SUFFIXES = [' Wall Art Decor', ' Wall Decor', ' Wall Art'];

  /**
   * Handle → CSS color (or gradient) lookup. Covers single colors AND
   * common-named combo colors. Unknown handles fall back to gray (with
   * the title visible via swatch tooltip, so the customer still
   * understands).
   *
   * Note: combo handles ending in `_and_*` are detected dynamically by
   * `colorBg()` and rendered as a diagonally-split circle, so we don't
   * need to enumerate every combo here — only the canonical single
   * colors.
   */
  const COLOR_HEX = {
    // Top-level
    beige: '#F5F0E6', black: '#000000', blue: '#3870D8', brown: '#8B5A2B',
    gold: '#D4AF37', gray: '#808080', grey: '#808080', green: '#3DA45A',
    orange: '#F08C2D', pink: '#F4A6B8', purple: '#8E5BB5', red: '#D33B3B',
    silver: '#C0C0C0', teal: '#1F8F8F', turquoise: '#3FD6CD',
    white: '#FFFFFF', yellow: '#F2C744', cream: '#F5EFD8',
    tan: '#D2B48C', navy: '#1B2A49', navy_blue: '#1B2A49',
    // Blue family
    royal_blue: '#2B45B5', sky_blue: '#75C4ED', light_blue: '#A8D5F0',
    cobalt_blue: '#0047AB', baby_blue: '#B8DCEC', dark_blue: '#1F2E55',
    powder_blue: '#B0D7E5', steel_blue: '#5079A0', cornflower_blue: '#6495ED',
    aquamarine: '#7FE6CC', midnight_blue: '#191970', azure: '#007FFF',
    indigo: '#4B0082', cerulean: '#007BA7', periwinkle: '#CCCCFF',
    // Green family
    sage: '#9CAF88', olive: '#7C7333', mint: '#A8E4C2', forest_green: '#1E4D2B',
    emerald: '#2E8B57', lime: '#C0E550', dark_green: '#1E3A1E',
    light_green: '#C2E8B0', hunter_green: '#355E3B', jade: '#00A86B',
    seafoam: '#9FE2BF', chartreuse: '#7FFF00', moss: '#8A9A5B',
    // Red / pink / orange
    crimson: '#9B1B30', coral: '#F08070', salmon: '#F88E72',
    peach: '#F8C09A', burgundy: '#6E1A1F', maroon: '#7A1F2F',
    rose: '#E37495', magenta: '#C7378A', fuchsia: '#D24FB7',
    rust: '#A55320', terracotta: '#B85A3F', amber: '#D89638',
    scarlet: '#FF2400', cherry: '#DE3163', vermillion: '#E34234',
    blush: '#DE5D83', hot_pink: '#FF69B4', dusty_rose: '#DCAE96',
    // Yellow family
    mustard: '#CFA84F', lemon: '#F7E26F', saffron: '#F4C430',
    canary: '#FFEF00', sunshine: '#FFD700',
    // Purple family
    lavender: '#C2B6D9', violet: '#A058C2', plum: '#7A3A6E',
    mauve: '#B784A7', orchid: '#DA70D6', lilac: '#C8A2C8',
    eggplant: '#614051',
    // Brown family
    chocolate: '#6F4023', coffee: '#553520', caramel: '#A86F3A',
    chestnut: '#7B3F2A', mahogany: '#62261A', mocha: '#7B5B3A',
    sand: '#D9C28F', ivory: '#F7EFD7', khaki: '#C3B091',
    sepia: '#704214',
    // Gray family
    charcoal: '#3D3D3D', slate: '#4F5A65', taupe: '#A88D7C',
    pewter: '#7D7C7D', warm_gray: '#A5A095', cool_gray: '#8A95A0',
    ash: '#B2BEB5', stone: '#928E85',
    // Metallics
    bronze: '#A06B3A', copper: '#B57050', rose_gold: '#D9A89A',
    platinum: '#CEC9C0', brass: '#B5A642', pewter_metal: '#919395',
    // Special / multi
    multicolor: 'linear-gradient(45deg,#F44 0%,#FA0 20%,#FF0 40%,#0C0 60%,#06F 80%,#80F 100%)',
    rainbow: 'linear-gradient(90deg,#F44,#FA0,#FF0,#0C0,#06F,#80F)',
    pastel: 'linear-gradient(135deg,#F4A6B8 0%,#C2B6D9 50%,#A8D5F0 100%)',
    neutral: '#D2C8B8', earth_tones: '#8B6B47',
    monochrome: 'linear-gradient(135deg,#000 0%,#FFF 100%)',
  };

  /* ─────────────────────────────────────────────────────────────────
   * Utility functions
   * ──────────────────────────────────────────────────────────────── */

  function cleanTitle(raw) {
    if (!raw) return '';
    let s = String(raw).trim();
    for (const suffix of TITLE_SUFFIXES) {
      if (s.toLowerCase().endsWith(suffix.toLowerCase())) {
        s = s.slice(0, -suffix.length).trim();
        break;
      }
    }
    return s;
  }

  /** Resolve a single color handle to a CSS color. Falls back to neutral
   *  gray for unknown handles (the tooltip still tells the customer
   *  what color it is). */
  function colorBgSingle(handle) {
    if (!handle) return '#CCCCCC';
    const h = String(handle).toLowerCase();
    if (COLOR_HEX[h]) return COLOR_HEX[h];
    // Try trailing words (e.g. `midnight_blue` → `blue`).
    const parts = h.split('_');
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const guess = parts.slice(i).join('_');
      if (COLOR_HEX[guess]) return COLOR_HEX[guess];
    }
    for (let i = 0; i < parts.length; i += 1) {
      if (COLOR_HEX[parts[i]]) return COLOR_HEX[parts[i]];
    }
    return '#CCCCCC';
  }

  /** Resolve a color handle to a CSS background. Handles combos like
   *  `black_and_blue` as a diagonal split (Bug #5b). */
  function colorBg(handle) {
    if (!handle) return '#CCCCCC';
    const h = String(handle).toLowerCase();
    // Direct hit (multicolor, rainbow, pastel, etc. may use the literal
    // handle even though they contain underscores).
    if (COLOR_HEX[h]) return COLOR_HEX[h];
    // Combo detection: split on `_and_` and render as diagonal halves.
    const comboMatch = h.match(/^(.+?)_and_(.+)$/);
    if (comboMatch) {
      const a = colorBgSingle(comboMatch[1]);
      const b = colorBgSingle(comboMatch[2]);
      // If either side is itself a gradient (multicolor, rainbow), skip
      // the diagonal split — too much visual noise.
      const aSimple = !a.startsWith('linear-gradient');
      const bSimple = !b.startsWith('linear-gradient');
      if (aSimple && bSimple) {
        return `linear-gradient(135deg, ${a} 0%, ${a} 50%, ${b} 50%, ${b} 100%)`;
      }
    }
    return colorBgSingle(h);
  }

  function getMeta() {
    const el = document.getElementById('WallArtFilterMeta');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch (_) { return null; }
  }

  function debounce(fn, ms) {
    let t = null;
    return function debounced(...args) {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; fn.apply(this, args); }, ms);
    };
  }

  /* ─────────────────────────────────────────────────────────────────
   * URL state
   * ──────────────────────────────────────────────────────────────── */

  function readUrlFilters() {
    const params = new URLSearchParams(window.location.search);
    const tag = new Set();
    const productType = new Set();
    let sortBy = '';
    let page = 1;
    for (const [k, v] of params.entries()) {
      if (k === 'filter.p.tag' && v) tag.add(v);
      else if (k === 'filter.p.product_type' && v) productType.add(v);
      else if (k === 'sort_by' && v) sortBy = v;
      else if (k === 'page' && v) {
        const n = parseInt(v, 10);
        if (n > 0) page = n;
      }
    }
    return { tag, productType, sortBy, page };
  }

  function writeUrlFilters(state) {
    const url = new URL(window.location.href);
    for (const k of Array.from(url.searchParams.keys())) {
      if (k === 'filter.p.tag' || k === 'filter.p.product_type' || k === 'sort_by' || k === 'page') {
        url.searchParams.delete(k);
      }
    }
    for (const v of state.tag) url.searchParams.append('filter.p.tag', v);
    for (const v of state.productType) url.searchParams.append('filter.p.product_type', v);
    if (state.sortBy) url.searchParams.set('sort_by', state.sortBy);
    if (state.page && state.page > 1) url.searchParams.set('page', String(state.page));
    window.history.pushState({}, '', url.toString());
  }

  /**
   * Build a list of visible pagination parts matching Shopify's pattern:
   * always show first + last, ±2 around current, "..." between gaps.
   * Examples (current=N, total=T):
   *   T=5, N=3   → [1,2,3,4,5]
   *   T=10, N=1  → [1,2,3,"...",10]
   *   T=10, N=5  → [1,"...",3,4,5,6,7,"...",10]
   *   T=245, N=1 → [1,2,3,"...",245]
   */
  function buildPaginationParts(current, total) {
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total]);
    for (let i = current - 2; i <= current + 2; i += 1) {
      if (i >= 1 && i <= total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i += 1) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
      result.push(sorted[i]);
    }
    return result;
  }

  /** Dawn's caret SVG (lifted from icon-caret.svg used by the
   *  pagination snippet). Used for prev/next arrows in pagination. */
  const CARET_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>';

  /* ─────────────────────────────────────────────────────────────────
   * Data fetching (in-memory cached)
   * ──────────────────────────────────────────────────────────────── */

  /**
   * Append a STATIC cache-bust param to a gallery-index URL (taxonomy.json /
   * collections/<handle>.json on GCS). The index objects were uploaded with a
   * 1-year immutable Cache-Control, so Google's edge cached the BARE URL (no
   * query) BEFORE the bucket's CORS rule existed — that copy is stale AND has
   * no `Access-Control-Allow-Origin`, so the browser blocks it. Adding any
   * query param routes around the poisoned bare-URL edge entry to a fresh,
   * CORS-enabled, `no-cache` object. Static token (`cb=2`) is correct: the
   * objects are now `no-cache`, so each busted URL revalidates (304) per load.
   * Must match the token in gallery-builder-lite's catalog-index.js.
   */
  const GALLERY_INDEX_CACHE_BUST = 'cb=2';
  function withCacheBust(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf(GALLERY_INDEX_CACHE_BUST) !== -1) return url;
    const sep = url.indexOf('?') !== -1 ? '&' : '?';
    return `${url}${sep}${GALLERY_INDEX_CACHE_BUST}`;
  }

  const _jsonCache = new Map();
  async function loadJson(url) {
    if (!url) throw new Error('loadJson: empty url');
    const bustedUrl = withCacheBust(url);
    if (_jsonCache.has(bustedUrl)) return _jsonCache.get(bustedUrl);
    const p = fetch(bustedUrl, { credentials: 'omit' }).then((r) => {
      if (!r.ok) throw new Error(`loadJson ${bustedUrl}: ${r.status}`);
      return r.json();
    });
    _jsonCache.set(bustedUrl, p);
    return p;
  }

  /**
   * Fetch matching Shopify product IDs for a search query, via the
   * alternate `templates/search.ids.liquid` view. Returns a Set<string>
   * of numeric product IDs (string-encoded to match the `i` field on
   * our index records).
   *
   * Why paginated: Shopify caps `{% paginate %}` at 50 for search.results,
   * so broad queries spread across multiple pages. We fetch page 1 to
   * learn the total page count, then parallel-fetch the rest (capped
   * at MAX_PAGES = 20 → up to 1000 matches). Queries that exceed the
   * cap return their first 1000 IDs; the customer can refine via the
   * filter dropdowns.
   *
   * Why `credentials: 'same-origin'`: the search endpoint may use the
   * customer session for personalization (rare for products, but
   * harmless to allow).
   *
   * Why .text() + JSON.parse: Shopify reserves the `.json` filename
   * extension for sections templates, so search.ids.liquid returns
   * text/html with a JSON body. The .liquid extension is a syntax
   * quirk, not a content-type signal.
   */
  const MAX_SEARCH_PAGES = 20;
  async function fetchSearchMatchIds(urlTemplate) {
    if (!urlTemplate) return null;
    const fetchPage = async (page) => {
      const url = urlTemplate.replace('{PAGE}', String(page));
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`search ids page ${page}: ${res.status}`);
      const text = (await res.text()).trim();
      return JSON.parse(text);
    };

    const first = await fetchPage(1);
    if (!first || !Array.isArray(first.ids)) return null;

    const ids = new Set(first.ids.map(String));
    const totalPages = Math.min(first.pages || 1, MAX_SEARCH_PAGES);
    if (totalPages > 1) {
      const rest = [];
      for (let p = 2; p <= totalPages; p++) rest.push(fetchPage(p).catch((e) => {
        console.warn(`[waf] search ids page ${p} failed:`, e);
        return null;
      }));
      const others = await Promise.all(rest);
      for (const r of others) {
        if (r && Array.isArray(r.ids)) {
          for (const id of r.ids) ids.add(String(id));
        }
      }
    }

    if (first.pages > MAX_SEARCH_PAGES) {
      console.warn(
        `[waf] search "${first.query}" returned ${first.total} results; ` +
        `capped at ${MAX_SEARCH_PAGES * 50}. Customer should refine query.`
      );
    }
    return ids;
  }

  /* ─────────────────────────────────────────────────────────────────
   * Taxonomy helpers (always use nodes_by_dim — see Bug #4)
   * ──────────────────────────────────────────────────────────────── */

  /** Get all root handles for a dimension, sorted by title. */
  function getDimRoots(taxonomy, dim) {
    if (!taxonomy) return [];
    const root = (taxonomy.roots || []).find((r) => r.dimension === dim);
    return root ? (root.children || []) : [];
  }

  /** Get a single node from the dimension-keyed map (NOT the flat map
   *  — that has cross-dim collisions, Bug #4). Falls back to the flat
   *  map (with dimension check) for backwards compat with old
   *  taxonomy.json files that don't yet have nodes_by_dim. */
  function getDimNode(taxonomy, dim, handle) {
    if (!taxonomy) return null;
    if (taxonomy.nodes_by_dim && taxonomy.nodes_by_dim[dim]) {
      return taxonomy.nodes_by_dim[dim][handle] || null;
    }
    if (taxonomy.nodes && taxonomy.nodes[handle]) {
      const node = taxonomy.nodes[handle];
      if (node && node.dimension === dim) return node;
    }
    return null;
  }

  /** Collect this handle + all descendant handles (transitive). */
  function collectDescendants(taxonomy, dim, handle) {
    const out = new Set([handle]);
    const q = [handle];
    while (q.length) {
      const h = q.shift();
      const node = getDimNode(taxonomy, dim, h);
      if (!node) continue;
      for (const child of node.children || []) {
        if (!out.has(child)) { out.add(child); q.push(child); }
      }
    }
    return out;
  }

  /* ─────────────────────────────────────────────────────────────────
   * Render: Subject dropdown (Bugs #1, #2, #4)
   * ──────────────────────────────────────────────────────────────── */

  /** Dual-mode Subject dropdown:
   *
   *  • NAVIGATION mode (on a Subject collection like /collections/animals-wall-art):
   *    show the current node + its descendant subjects as clickable
   *    <a> links. Clicking a row navigates to that subject's collection.
   *    The customer is already "in" a subject — narrowing by subject
   *    inside it doesn't make sense; drilling deeper does.
   *
   *  • FILTER mode (on color/room/style collections, or the synthetic
   *    "all" collection): show all subject roots as cascade checkboxes,
   *    same UX as the Style dropdown. The customer can narrow the
   *    current color/room/style result set by one or more subjects.
   *    Tag values are `subject:<handle>` so the existing filter
   *    pipeline handles them with zero changes.
   *
   *  Dimension detection uses `state.collectionDimension` which the
   *  publisher writes into each per-collection JSON.
   */
  function renderSubject(state, container) {
    container.innerHTML = '';

    const isSubjectCollection = state.collectionDimension === 'subject' && !!state.collectionTagValue;

    if (!isSubjectCollection) {
      // Filter mode — reuse the same component the Style dropdown uses,
      // just pointed at the subject dimension. Cascade + checkbox UX is
      // identical (parent checks all descendants).
      renderFilterTree(state, container, 'subject');
      return;
    }

    // Navigation mode — current subject's descendant tree (clickable
    // <a> rows). Mirrors the original behavior.
    const node = getDimNode(state.taxonomy, 'subject', state.collectionTagValue);
    if (!node) {
      // Fall back to roots if the current tag isn't in the taxonomy
      // (stale collection, off-by-one handle). Still nav mode.
      const roots = getDimRoots(state.taxonomy, 'subject');
      if (roots.length === 0) {
        container.innerHTML = '<div class="waf-dropdown__empty">Konu bulunamadı.</div>';
        return;
      }
      const tree = document.createElement('div');
      tree.className = 'waf-subject-tree';
      for (const handle of roots) {
        const n = getDimNode(state.taxonomy, 'subject', handle);
        if (n) tree.appendChild(buildSubjectBranch(state, n, 0, false));
      }
      container.appendChild(tree);
      wireSubjectChevrons(container);
      return;
    }

    const tree = document.createElement('div');
    tree.className = 'waf-subject-tree';
    tree.appendChild(buildSubjectBranch(state, node, 0, true));
    container.appendChild(tree);
    wireSubjectChevrons(container);
  }

  /** Build a subject branch <div> recursively. Renders the node itself
   *  as a navigable <a> + (if it has children) a chevron button that
   *  toggles a collapsible children container. Default state:
   *  collapsed (the customer expands what they want to drill into). */
  function buildSubjectBranch(state, node, depth, isCurrent) {
    const branch = document.createElement('div');
    branch.className = 'waf-subject-row';
    branch.style.setProperty('--waf-depth', String(depth));
    if (isCurrent) branch.classList.add('is-current');

    const head = document.createElement('div');
    head.className = 'waf-subject-row__head';

    // Best-effort URL handle — see guessCollectionUrl().
    const urlHandle = guessCollectionUrl(node.handle, node.title);
    const link = document.createElement('a');
    link.className = 'waf-subject-row__link';
    link.href = urlHandle;
    link.textContent = cleanTitle(node.title);
    head.appendChild(link);

    const children = (node.children || []).map((h) => getDimNode(state.taxonomy, 'subject', h)).filter(Boolean);
    if (children.length > 0) {
      const chev = document.createElement('button');
      chev.type = 'button';
      chev.className = 'waf-subject-row__chevron';
      chev.setAttribute('aria-expanded', 'false');
      chev.setAttribute('aria-label', `Expand ${cleanTitle(node.title)}`);
      chev.innerHTML = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M3 4l3 3 3-3" stroke="currentColor" fill="none" stroke-width="1.6" stroke-linecap="round"/></svg>';
      head.appendChild(chev);
    }
    branch.appendChild(head);

    if (children.length > 0) {
      const kids = document.createElement('div');
      kids.className = 'waf-subject-row__children';
      kids.hidden = true; // collapsed by default
      for (const child of children) {
        kids.appendChild(buildSubjectBranch(state, child, depth + 1, false));
      }
      branch.appendChild(kids);
    }

    return branch;
  }

  /** Wire each subject branch's chevron to toggle its children
   *  container. Plain-DOM event delegation kept inline since the
   *  subject tree is re-rendered fresh each open. */
  function wireSubjectChevrons(container) {
    container.querySelectorAll('.waf-subject-row__chevron').forEach((chev) => {
      chev.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const branch = chev.closest('.waf-subject-row');
        if (!branch) return;
        const kids = branch.querySelector(':scope > .waf-subject-row__children');
        if (!kids) return;
        const expanded = chev.getAttribute('aria-expanded') === 'true';
        chev.setAttribute('aria-expanded', String(!expanded));
        kids.hidden = expanded;
      });
    });
  }

  /** Best-effort URL for a subject collection given its taxonomy handle
   *  and title. Mirrors `canonicalCollectionHandle` in
   *  electron/services/shopify-client.ts.
   *
   *  Note: this does string surgery rather than read from an
   *  authoritative source, because we don't ship the collection URL
   *  map to the snippet. If a merchant manually renamed a collection's
   *  handle, this guess will be wrong (customer hits a 404). Acceptable
   *  trade-off for v1 — most collections follow the canonical pattern. */
  function guessCollectionUrl(handle, title) {
    const slug = (title || handle || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return `/collections/${slug}`;
  }

  /* ─────────────────────────────────────────────────────────────────
   * Render: Koleksiyon (ODA_LITE — collection navigation)
   *
   * The lite taxonomy's `koleksiyon` dim is a flat (or shallow) tree of
   * Turkish collections. We render each as a navigation <a> straight to
   * `/collections/<handle>` (the node's own handle, which the lite
   * publisher sets to the real Shopify collection handle). The current
   * collection gets `.is-current`. Reuses the subject-row CSS classes.
   * ──────────────────────────────────────────────────────────────── */

  function koleksiyonCollectionUrl(node) {
    const handle = node && typeof node.handle === 'string' ? node.handle.trim() : '';
    if (handle) return `/collections/${handle}`;
    return guessCollectionUrl(node && node.handle, node && node.title);
  }

  function buildKoleksiyonBranch(state, node, depth, currentHandle) {
    const branch = document.createElement('div');
    branch.className = 'waf-subject-row';
    branch.style.setProperty('--waf-depth', String(depth));
    if (node.handle && node.handle === currentHandle) branch.classList.add('is-current');

    const head = document.createElement('div');
    head.className = 'waf-subject-row__head';
    const link = document.createElement('a');
    link.className = 'waf-subject-row__link';
    link.href = koleksiyonCollectionUrl(node);
    link.textContent = cleanTitle(node.title);
    head.appendChild(link);

    const children = (node.children || [])
      .map((h) => getDimNode(state.taxonomy, 'koleksiyon', h))
      .filter(Boolean);
    if (children.length > 0) {
      const chev = document.createElement('button');
      chev.type = 'button';
      chev.className = 'waf-subject-row__chevron';
      chev.setAttribute('aria-expanded', 'false');
      chev.setAttribute('aria-label', `${cleanTitle(node.title)}`);
      chev.innerHTML = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M3 4l3 3 3-3" stroke="currentColor" fill="none" stroke-width="1.6" stroke-linecap="round"/></svg>';
      head.appendChild(chev);
    }
    branch.appendChild(head);

    if (children.length > 0) {
      const kids = document.createElement('div');
      kids.className = 'waf-subject-row__children';
      kids.hidden = true;
      for (const child of children) {
        kids.appendChild(buildKoleksiyonBranch(state, child, depth + 1, currentHandle));
      }
      branch.appendChild(kids);
    }
    return branch;
  }

  function renderKoleksiyon(state, container) {
    container.innerHTML = '';
    const roots = getDimRoots(state.taxonomy, 'koleksiyon');
    if (roots.length === 0) {
      container.innerHTML = '<div class="waf-dropdown__empty">Koleksiyon bulunamadı.</div>';
      return;
    }
    const currentHandle = state.collectionTagValue || (state.meta && state.meta.current_collection_handle) || '';
    const tree = document.createElement('div');
    tree.className = 'waf-subject-tree';
    for (const handle of roots) {
      const node = getDimNode(state.taxonomy, 'koleksiyon', handle);
      if (!node) continue;
      tree.appendChild(buildKoleksiyonBranch(state, node, 0, currentHandle));
    }
    container.appendChild(tree);
    wireSubjectChevrons(container);
  }

  /** Does this taxonomy use the lite Turkish dims (koleksiyon + renk)? */
  function isLiteTaxonomy(taxonomy) {
    if (!taxonomy) return false;
    if (taxonomy.nodes_by_dim && (taxonomy.nodes_by_dim.koleksiyon || taxonomy.nodes_by_dim.renk)) return true;
    return (taxonomy.roots || []).some((r) => r.dimension === 'koleksiyon' || r.dimension === 'renk');
  }

  /* ─────────────────────────────────────────────────────────────────
   * Render: Color swatches (Bug #5)
   * ──────────────────────────────────────────────────────────────── */

  /**
   * The "main" colors — rainbow + common neutrals — get top placement
   * in the swatch grid. Order here is the visual order in the grid.
   * Handles not in this list are bucketed as "other singles" (next),
   * then combos last.
   */
  const MAIN_COLOR_ORDER = [
    'red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink',
    'brown', 'beige', 'tan', 'cream', 'gold', 'silver',
    'black', 'white', 'gray',
  ];

  /** Resolve a color handle to its display CSS. Returns null when the
   *  handle (or, for combos, both halves) is unknown — used to skip
   *  the gray-fallback swatches from the dropdown entirely. */
  function colorBgIfKnown(handle) {
    if (!handle) return null;
    const h = String(handle).toLowerCase();
    if (COLOR_HEX[h]) return COLOR_HEX[h];
    const comboMatch = h.match(/^(.+?)_and_(.+)$/);
    if (comboMatch) {
      const a = COLOR_HEX[comboMatch[1]];
      const b = COLOR_HEX[comboMatch[2]];
      if (a && b && !a.startsWith('linear-gradient') && !b.startsWith('linear-gradient')) {
        return `linear-gradient(135deg, ${a} 0%, ${a} 50%, ${b} 50%, ${b} 100%)`;
      }
    }
    return null;
  }

  /** ODA_LITE: the color dimension is `renk` (Turkish) in the lite
   *  taxonomy and `color` in the English one. Detect whichever exists. */
  function colorDimName(taxonomy) {
    if (taxonomy && taxonomy.nodes_by_dim) {
      if (taxonomy.nodes_by_dim.renk) return 'renk';
      if (taxonomy.nodes_by_dim.color) return 'color';
    }
    const hasRoot = (d) => (taxonomy?.roots || []).some((r) => r.dimension === d);
    if (hasRoot('renk')) return 'renk';
    return 'color';
  }

  /** Background CSS for a color node. ODA_LITE: `renk` nodes carry a `hex`
   *  field straight from the publisher (authoritative), so prefer that;
   *  fall back to the English COLOR_HEX lookup for backwards compat. */
  function colorBgForNode(node, handle) {
    const hex = node && typeof node.hex === 'string' ? node.hex.trim() : '';
    if (hex) return hex.charAt(0) === '#' || hex.startsWith('linear-gradient') ? hex : `#${hex}`;
    return colorBgIfKnown(handle);
  }

  function renderColorSwatches(state, container) {
    container.innerHTML = '';
    const colorDim = colorDimName(state.taxonomy);
    const roots = getDimRoots(state.taxonomy, colorDim);
    // Flatten taxonomy → all color handles, deduped.
    const handles = [];
    const seen = new Set();
    for (const root of roots) {
      const queue = [root];
      while (queue.length) {
        const h = queue.shift();
        if (seen.has(h)) continue;
        seen.add(h);
        const node = getDimNode(state.taxonomy, colorDim, h);
        if (!node) continue;
        handles.push(h);
        for (const c of node.children || []) queue.push(c);
      }
    }

    // Filter out handles we can't render visually (gray-fallback noise).
    // ODA_LITE `renk` nodes carry their own `hex`, so they always render.
    const visible = handles.filter((h) => colorBgForNode(getDimNode(state.taxonomy, colorDim, h), h) !== null);

    // Sort into: main colors (in MAIN_COLOR_ORDER), then other singles
    // (alphabetical), then combos (alphabetical). Combos identified by
    // the `_and_` separator.
    visible.sort((a, b) => {
      const aCombo = /_and_/.test(a);
      const bCombo = /_and_/.test(b);
      if (aCombo !== bCombo) return aCombo ? 1 : -1;
      const aMain = MAIN_COLOR_ORDER.indexOf(a);
      const bMain = MAIN_COLOR_ORDER.indexOf(b);
      if (aMain !== -1 && bMain !== -1) return aMain - bMain;
      if (aMain !== -1) return -1;
      if (bMain !== -1) return 1;
      const ta = (getDimNode(state.taxonomy, colorDim, a) || {}).title || a;
      const tb = (getDimNode(state.taxonomy, colorDim, b) || {}).title || b;
      return ta.localeCompare(tb);
    });

    if (visible.length === 0) {
      container.innerHTML = '<div class="waf-dropdown__empty">Renk bulunamadı.</div>';
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'waf-swatches';
    for (const handle of visible) {
      const node = getDimNode(state.taxonomy, colorDim, handle);
      const title = cleanTitle(node ? node.title : handle);
      const bg = colorBgForNode(node, handle);
      const label = document.createElement('label');
      label.className = 'waf-swatch';
      label.title = title; // native browser tooltip as a fallback
      const swatchVisual = document.createElement('span');
      swatchVisual.className = 'waf-swatch__visual';
      swatchVisual.style.background = bg;
      // Light colors need an inner border so they're visible on the
      // dropdown's white background.
      if (/^#(?:F[0-9A-F]|EF|FF)/i.test(bg)) {
        swatchVisual.classList.add('waf-swatch__visual--light');
      }
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'waf-swatch__input';
      input.name = 'filter.p.tag';
      // Tag value is `<colorDim>:<handle>` so the dim-agnostic matcher
      // buckets it correctly (lite index tags products as `renk:<handle>`).
      input.value = `${colorDim}:${handle}`;
      input.dataset.wafFilterInput = '1';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'waf-swatch__name';
      nameSpan.textContent = title;
      label.appendChild(input);
      label.appendChild(swatchVisual);
      label.appendChild(nameSpan);
      grid.appendChild(label);
    }
    container.appendChild(grid);
  }

  /* ─────────────────────────────────────────────────────────────────
   * Render: Room (flat checklist)
   * ──────────────────────────────────────────────────────────────── */

  function renderRoomChecklist(state, container) {
    container.innerHTML = '';
    const roots = getDimRoots(state.taxonomy, 'room');
    if (roots.length === 0) {
      container.innerHTML = '<div class="waf-dropdown__empty">Oda bulunamadı.</div>';
      return;
    }
    for (const handle of roots) {
      const node = getDimNode(state.taxonomy, 'room', handle);
      if (!node) continue;
      const label = document.createElement('label');
      label.className = 'waf-check';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'filter.p.tag';
      input.value = `room:${handle}`;
      input.dataset.wafFilterInput = '1';
      const text = document.createElement('span');
      text.className = 'waf-check__label';
      text.textContent = cleanTitle(node.title);
      label.appendChild(input);
      label.appendChild(text);
      container.appendChild(label);
    }
  }

  /* ─────────────────────────────────────────────────────────────────
   * Render: Style tree (Bugs #3, #4)
   * ──────────────────────────────────────────────────────────────── */

  function renderStyleTree(state, container) {
    renderFilterTree(state, container, 'style');
  }

  /** Render a checkbox+cascade filter tree for any dimension. Used by
   *  Style always; used by Subject when the current collection is NOT
   *  itself a subject (so the customer can narrow a color/room/style
   *  collection by subject). */
  function renderFilterTree(state, container, dimension) {
    container.innerHTML = '';
    const roots = getDimRoots(state.taxonomy, dimension);
    if (roots.length === 0) {
      container.innerHTML = `<div class="waf-dropdown__empty">No ${dimension}s available.</div>`;
      return;
    }
    const tree = document.createElement('div');
    tree.className = 'waf-filter-tree';
    for (const handle of roots) {
      const node = getDimNode(state.taxonomy, dimension, handle);
      if (!node) continue;
      tree.appendChild(buildFilterTreeBranch(state, node, 0, dimension));
    }
    container.appendChild(tree);
    wireStyleTreeChevrons(container);
  }

  /** Build one filter-tree branch (was buildStyleBranch — now generalized
   *  to any dimension via the `dimension` arg). Each branch:
   *    - has its own checkbox (selectable)
   *    - if it has children: chevron button + collapsible children
   *      container; checking the parent cascades to descendants
   *  The cascade is implemented in the form's change handler — see
   *  WafFilterForm.onFormChange. We just mark the branch with
   *  data-waf-cascade-branch + the parent input with
   *  data-waf-cascade-parent so the handler knows what to do. */
  function buildFilterTreeBranch(state, node, depth, dimension) {
    const branch = document.createElement('div');
    branch.className = 'waf-tree-branch';
    branch.dataset.wafCascadeBranch = '1';
    branch.style.setProperty('--waf-depth', String(depth));

    const head = document.createElement('div');
    head.className = 'waf-tree-row';

    const label = document.createElement('label');
    label.className = 'waf-check';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = 'filter.p.tag';
    input.value = `${dimension}:${node.handle}`;
    input.dataset.wafFilterInput = '1';
    const childHandles = (node.children || []).map((h) => getDimNode(state.taxonomy, dimension, h)).filter(Boolean);
    if (childHandles.length > 0) input.dataset.wafCascadeParent = '1';
    const text = document.createElement('span');
    text.className = 'waf-check__label';
    text.textContent = cleanTitle(node.title);
    label.appendChild(input);
    label.appendChild(text);
    head.appendChild(label);

    if (childHandles.length > 0) {
      const chev = document.createElement('button');
      chev.type = 'button';
      chev.className = 'waf-tree-chevron';
      chev.setAttribute('aria-expanded', 'false');
      chev.setAttribute('aria-label', `Expand ${cleanTitle(node.title)}`);
      chev.innerHTML = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M3 4l3 3 3-3" stroke="currentColor" fill="none" stroke-width="1.5"/></svg>';
      head.appendChild(chev);
    }
    branch.appendChild(head);

    if (childHandles.length > 0) {
      const kids = document.createElement('div');
      kids.className = 'waf-tree-children';
      kids.hidden = true;
      for (const child of childHandles) {
        kids.appendChild(buildFilterTreeBranch(state, child, depth + 1, dimension));
      }
      branch.appendChild(kids);
    }
    return branch;
  }

  /** Wire up chevron clicks on a style tree — toggle the corresponding
   *  children container. Done after render. */
  function wireStyleTreeChevrons(container) {
    container.querySelectorAll('.waf-tree-chevron').forEach((chev) => {
      chev.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const branch = chev.closest('.waf-tree-branch');
        if (!branch) return;
        const kids = branch.querySelector(':scope > .waf-tree-children');
        if (!kids) return;
        const expanded = chev.getAttribute('aria-expanded') === 'true';
        chev.setAttribute('aria-expanded', String(!expanded));
        kids.hidden = expanded;
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * Filtering
   * ──────────────────────────────────────────────────────────────── */

  /**
   * Sort a (already filtered) product array by the chosen sort mode.
   *   featured     → is_featured first, then ads-priority (`a`), then like count desc, then sales desc
   *   most_liked   → like count desc, then featured, then sales desc
   *   best_selling → sales desc, then like count desc, then featured
   * Tie-break by `h` (handle) for stable ordering across reloads.
   * Returns a new array (doesn't mutate input).
   */
  function sortProducts(products, sortBy) {
    const arr = products.slice();
    const cmpHandle = (a, b) => (a.h < b.h ? -1 : a.h > b.h ? 1 : 0);
    if (sortBy === 'most_liked') {
      arr.sort((a, b) =>
        ((b.l || 0) - (a.l || 0)) ||
        ((b.f ? 1 : 0) - (a.f ? 1 : 0)) ||
        ((b.s || 0) - (a.s || 0)) ||
        cmpHandle(a, b),
      );
    } else if (sortBy === 'best_selling') {
      arr.sort((a, b) =>
        ((b.s || 0) - (a.s || 0)) ||
        ((b.l || 0) - (a.l || 0)) ||
        ((b.f ? 1 : 0) - (a.f ? 1 : 0)) ||
        cmpHandle(a, b),
      );
    } else {
      // featured (default + fallback for unknown sort values):
      // Featured badge first, then ads-priority (`a` = Ads Review "yes"
      // designs — lifted in the grid WITHOUT a badge), then likes, then sales.
      arr.sort((a, b) =>
        ((b.f ? 1 : 0) - (a.f ? 1 : 0)) ||
        ((b.a ? 1 : 0) - (a.a ? 1 : 0)) ||
        ((b.l || 0) - (a.l || 0)) ||
        ((b.s || 0) - (a.s || 0)) ||
        cmpHandle(a, b),
      );
    }
    return arr;
  }

  /** AND across dimensions; OR within a dimension. Pieces is its own
   *  dimension via filter.p.product_type. */
  function filterProducts(products, urlState) {
    // Bucket tag selections by their dim prefix.
    const tagByDim = {}; // { color: Set, room: Set, style: Set, subject: Set, ... }
    for (const v of urlState.tag) {
      const idx = v.indexOf(':');
      if (idx < 0) continue;
      const dim = v.slice(0, idx);
      if (!tagByDim[dim]) tagByDim[dim] = new Set();
      tagByDim[dim].add(v);
    }
    const dims = Object.keys(tagByDim);
    return products.filter((p) => {
      // Each dim must match at least one of its selected tags.
      for (const dim of dims) {
        const wanted = tagByDim[dim];
        let hit = false;
        for (const t of p.c) {
          if (wanted.has(t)) { hit = true; break; }
        }
        if (!hit) return false;
      }
      // Pieces (product_type).
      if (urlState.productType.size > 0 && !urlState.productType.has(p.y)) return false;
      return true;
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * Grid rendering (matches Dawn's card-product output structure)
   * ──────────────────────────────────────────────────────────────── */

  function buildCardEl(product) {
    const li = document.createElement('li');
    li.className = 'grid__item';
    // Title is already the short display name (matches custom.display_name
    // metafield) when the publisher is up-to-date. cleanTitle strips any
    // " Wall Art" / " Wall Decor" suffix that slipped through.
    const title = cleanTitle(product.t || '');
    const titleEsc = escapeHtml(title);
    const href = `/products/${product.h}`;
    const formatLabel = product.y === 'Wall Art Set' ? 'Wall Set' : 'Wall Art';
    const productId = product.i || '';
    const designId = product.d || '';
    // Display image at CARD_IMAGE_WIDTH only (no srcset). srcset would
    // make the browser pick different widths per viewport/DPR, which
    // would break our prefetch + preload (URL mismatch → cache miss).
    // A single src means every preload, prefetch, and rendered IMG
    // resolves to the SAME URL → guaranteed cache hit on paint.
    // Heart's data-product-image: 300w (matches card-product.liquid).
    const imgSrcDisplay = resizedImageUrl(product.img, CARD_IMAGE_WIDTH);
    const imgSrcHeart = resizedImageUrl(product.img, 300);

    // Featured badge — appears top-left when the product carries the
    // `featured` flag (set via the Featured tab in Darphane, mirrored
    // to Shopify as the `featured` product tag).
    const featuredBadge = product.f
      ? `<span class="waf-card-badge waf-card-badge--featured" aria-label="Featured product">★ Featured</span>`
      : '';

    // Wishlist starting boost. The publisher emits `b` separately from
    // `l` (which is boost + live_at_publish). The `b` value goes into
    // the heart button's `data-boost` attribute so wishlist.js adds it
    // on top of the Worker-fetched live count when overwriting the
    // displayed number — without this the boost would visibly vanish
    // the moment wishlist.js hydrates (Worker counts only real
    // wishlists, no boost). Initial display uses `l` (boost + live at
    // publish) so the user sees the boosted count even before
    // wishlist.js fires; the JS then refines it to (boost + live_now).
    const boost = Number(product.b) || 0;
    const initialLikeCount = Number(product.l) || 0;

    // ODA_LITE: match card-product.liquid — portrait (7:10) image + title +
    // price. No wishlist heart, no "More like this" (the collection card has
    // neither). Uses Dawn card classes so the collection/search section CSS
    // styles it identically. Price uses the shop money_format ({{amount}}TL).
    const priceHtml = (product.p != null && product.p !== '')
      ? `<div class="card-information"><div class="price"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${formatMoney(product.p)}</span></div></div></div></div>`
      : '';
    li.innerHTML = `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media" style="--ratio-percent: 142.857%;">
          <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 142.857%;">
            ${featuredBadge}
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  src="${escapeHtml(imgSrcDisplay)}"
                  alt="${titleEsc}"
                  class="motion-reduce"
                  decoding="async"
                  loading="lazy"
                  width="1400"
                  height="2000"
                >
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${href}" class="full-unstyled-link">${titleEsc}</a>
                </h3>
              </div>
              <div class="card__badge bottom left"></div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${href}" class="full-unstyled-link">${titleEsc}</a>
              </h3>
              ${priceHtml}
            </div>
          </div>
        </div>
      </div>
    `;
    return li;
  }

  // Oda money_format is "{{amount}}TL": US-style number (comma thousands,
  // period decimal, 2 decimals) + "TL" with no space. Match it exactly so
  // the JS-rendered price equals the server-rendered card-product price.
  function formatMoney(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'TL';
  }

  function escapeHtml(s) {
    if (!s) return '';
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  /**
   * Resize a hero image URL to the requested width. Two paths:
   *
   *   1. Shopify CDN URLs (`cdn.shopify.com/…?v=…`): append
   *      `&width=N` (Shopify's native image-CDN resize). Fast
   *      everywhere, multiple PoPs, purpose-built for storefronts.
   *      Preserves the version stamp so cache busts when the image
   *      changes.
   *
   *   2. GCS URLs (or anything else): route through images.weserv.nl —
   *      a free public resize proxy (Cloudflare-backed). Slower first
   *      hit (~500ms), still CDN-cached on subsequent requests. Used
   *      as a fallback for the small set of products whose Shopify
   *      featuredImage hasn't been captured yet.
   *
   * The publisher prefers Shopify CDN URLs when available (see
   * `fetchAllProductFeaturedImages` in shopify-client.ts), so in
   * practice path #1 is the common case.
   */
  function resizedImageUrl(srcUrl, width) {
    if (!srcUrl || typeof srcUrl !== 'string') return srcUrl || '';
    if (srcUrl.indexOf('cdn.shopify.com') !== -1) {
      // Strip any existing width param, then append a fresh one. Don't
      // touch other params (v= cache-bust must survive).
      const cleaned = srcUrl.replace(/[?&]width=\d+/g, '');
      const sep = cleaned.indexOf('?') !== -1 ? '&' : '?';
      return `${cleaned}${sep}width=${width}`;
    }
    const m = srcUrl.match(/^https?:\/\/(.+)$/);
    if (!m) return srcUrl;
    return `https://images.weserv.nl/?url=${encodeURIComponent(m[1])}&w=${width}&output=webp&q=82`;
  }

  /**
   * Wait for every <img> within a given root element (or array of
   * elements) to finish loading. Uses the REAL <img> elements that
   * will be displayed — so the load URL matches exactly (srcset +
   * sizes selection is identical to what the card would have done
   * once attached to DOM).
   *
   * Why not `new Image()` preload? Because the card uses `srcset` and
   * the browser picks a different width depending on viewport + DPR.
   * A `new Image().src = …533w…` preload prefetches one URL; the card
   * later fetches a 720w (or whatever) and the cache key misses.
   * Building the actual cards off-DOM and waiting for THEIR <img>s
   * means the URL we wait on is the URL that paints — guaranteed cache
   * hit when we attach them.
   *
   * Safety: 5-second timeout caps the wait so a broken/slow CDN miss
   * can't strand the customer. Errors resolve too (broken images
   * shouldn't block — they'll render as broken-image-icons).
   */
  function waitForImagesIn(roots) {
    const rootList = Array.isArray(roots) ? roots : [roots];
    const imgs = [];
    for (const r of rootList) {
      if (!r) continue;
      r.querySelectorAll('img').forEach((img) => imgs.push(img));
    }
    if (imgs.length === 0) return Promise.resolve();
    const promises = imgs.map((img) => new Promise((resolve) => {
      if (img.complete && img.naturalWidth > 0) { resolve(); return; }
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    }));
    return Promise.race([
      Promise.all(promises),
      new Promise((r) => setTimeout(r, 5000)),
    ]);
  }

  /* ─────────────────────────────────────────────────────────────────
   * The custom element — orchestrates everything
   * ──────────────────────────────────────────────────────────────── */

  class WafFilterForm extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._state = {
        meta: null,
        taxonomy: null,
        collectionIndex: null,
        allProducts: [], // sliced reference
        filtered: [],    // last filter result
        renderedCount: 0,
        observer: null,
        collectionDimension: '',
        collectionTagValue: '',
      };
      this._form = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._form = this.querySelector('form[data-waf-form]');
      if (!this._form) return;

      // Wire dropdown UX (close-others on open, close on outside click).
      setupDropdownBehavior(this);

      // Wire sort select → hidden mirror → URL. Initialize the select
      // from the URL's `sort_by` param so reloads + back/forward
      // preserve the chosen sort.
      const sortSelect = this.querySelector('[data-waf-sort-select]');
      const sortMirror = this.querySelector('[data-waf-sort-mirror]');
      const initialSort = readUrlFilters().sortBy || 'featured';
      if (sortSelect) sortSelect.value = initialSort;
      if (sortMirror) sortMirror.value = initialSort;
      if (sortSelect && sortMirror) {
        sortSelect.addEventListener('change', () => {
          sortMirror.value = sortSelect.value;
          this.onFormChange({ target: sortMirror });
        });
      }

      this._state.meta = getMeta();
      if (!this._state.meta) {
        console.warn('[waf] missing meta JSON — filter snippet inert');
        return;
      }

      // Initial filter state from URL (so reload preserves selection).
      this._urlState = readUrlFilters();

      // ODA_LITE: on the DEFAULT collection view (no active color filter, no
      // explicit sort, not search) keep Shopify's server-rendered grid,
      // pagination and native order EXACTLY as-is — don't take over the grid
      // or show a spinner. Just background-load the index so the FIRST
      // filter/sort is instant. Any active filter/sort (or the search page)
      // takes over immediately, forcing a spinner to hide the stale/unfiltered
      // server cards during the fetch.
      if (this.isDefaultView()) {
        this.fetchAllAndHydrate().catch((err) => {
          console.error('[waf] init failed:', err);
        });
      } else {
        this.showLoadingSpinner(true);
        this.fetchAllAndHydrate().catch((err) => {
          console.error('[waf] init failed:', err);
          this.showLoadError();
        });
      }
    }

    /** Drop a spinner into the grid + remove the server-rendered
     *  pagination wrapper (its page count was for the unfiltered set
     *  and we'd be misleading the customer to leave it visible). */
    /** True for a plain collection view with no active color filter, no
     *  explicit sort, and not search — the grid should then stay exactly as
     *  Shopify server-rendered it (native order + pagination), and we skip the
     *  JS takeover entirely until the customer actually filters/sorts. */
    isDefaultView() {
      const meta = this._state.meta || {};
      if (meta.is_search) return false;
      const u = this._urlState || {};
      const noFilters = (!u.tag || u.tag.size === 0) && (!u.productType || u.productType.size === 0);
      const noSort = !u.sortBy || u.sortBy === 'featured';
      return noFilters && noSort;
    }

    /** Called after a filter/sort change lands back on the bare default
     *  collection view (customer removed their last filter). We can't
     *  reproduce Shopify's native collection order client-side — the
     *  server only ships page 1 in the DOM and the index JSON isn't in
     *  collection sort order — so hand the grid back to Shopify by doing a
     *  full navigation to the clean collection URL (same target as the
     *  "Tümünü temizle" link). That re-renders the native server grid with
     *  its native order + pagination, instead of our JS grid in index
     *  order. Returns true if it navigated (caller should bail). */
    restoreNativeGridIfDefault() {
      if (!this.isDefaultView()) return false;
      const clear = this.querySelector('[data-waf-clear-all]');
      const href = (clear && clear.getAttribute('href')) || window.location.pathname;
      window.location.assign(href);
      return true;
    }

    showLoadingSpinner(force) {
      const grid = document.getElementById('product-grid');
      // Show the spinner when FORCED (initial takeover of a filtered/search
      // view — hides the stale/unfiltered server cards during the fetch), or
      // when the grid is empty. On interactive re-renders (force falsy, grid
      // already has cards) keep them and let renderGrid swap synchronously —
      // no spinner flash.
      if (grid && (force || !grid.querySelector('.grid__item'))) {
        grid.innerHTML = '<li class="waf-grid-loading" aria-live="polite"><span class="waf-grid-loading__spinner" role="status" aria-label="Ürünler yükleniyor"></span></li>';
      }
      const container = document.getElementById('ProductGridContainer');
      const oldPagination = container && container.querySelector('.pagination-wrapper');
      if (oldPagination) oldPagination.remove();
    }

    /** Fallback for the very-rare case the index fetch errors out
     *  (offline, GCS down, metafield missing). Show a friendly message
     *  with a reload link instead of a permanent spinner. */
    showLoadError() {
      const grid = document.getElementById('product-grid');
      if (!grid) return;
      grid.innerHTML = `<li class="grid__item waf-grid-empty"><div class="waf-grid-empty__body"><h2>Ürünler yüklenemedi.</h2><p>Lütfen <a href="${window.location.href}">tekrar deneyin</a>.</p></div></li>`;
    }

    async fetchAllAndHydrate() {
      const { taxonomy_url, collection_index_url, is_search, search_ids_url_template } = this._state.meta;
      const tasks = [];
      if (taxonomy_url) tasks.push(loadJson(taxonomy_url).catch((e) => { console.warn('[waf] taxonomy fetch failed:', e); return null; }));
      else tasks.push(Promise.resolve(null));
      if (collection_index_url) tasks.push(loadJson(collection_index_url).catch((e) => { console.warn('[waf] collection index fetch failed:', e); return null; }));
      else tasks.push(Promise.resolve(null));
      // Search context: in parallel with the index, fetch the set of
      // product IDs Shopify's built-in search matched for this query.
      // We intersect this with allProducts (which is `all.json` on
      // search pages — see the snippet's current_handle fallback).
      if (is_search && search_ids_url_template) {
        tasks.push(fetchSearchMatchIds(search_ids_url_template).catch((e) => {
          console.warn('[waf] search ids fetch failed:', e);
          return null;
        }));
      } else {
        tasks.push(Promise.resolve(null));
      }
      const [taxonomy, collectionIndex, searchMatchIds] = await Promise.all(tasks);
      this._state.taxonomy = taxonomy;
      this._state.collectionIndex = collectionIndex;
      if (collectionIndex && Array.isArray(collectionIndex.products)) {
        let products = collectionIndex.products;
        // Search intersection: restrict allProducts to the IDs Shopify
        // search matched. If the fetch failed (searchMatchIds null),
        // fall back to showing the full unfiltered all.json — the
        // customer still gets a usable grid, just without the search
        // match cut. This matches the existing "filtering temporarily
        // unavailable" graceful-degradation pattern below.
        if (is_search && searchMatchIds && searchMatchIds.size > 0) {
          products = products.filter((p) => searchMatchIds.has(String(p.i)));
          console.log(
            `[waf] search "${this._state.meta.search_query}" → ${searchMatchIds.size} Shopify matches, ` +
            `${products.length} after intersect with all.json`
          );
        } else if (is_search) {
          // Search active but no IDs (fetch failed OR Shopify returned
          // 0 matches). Show empty grid + a help line so the customer
          // isn't confused by an unfiltered full catalog.
          products = [];
        }
        this._state.allProducts = products;
        this._state.collectionDimension = collectionIndex.dimension || '';
        this._state.collectionTagValue = collectionIndex.tag_value || '';
        this._state.hasIndex = true;
      } else {
        this._state.hasIndex = false;
      }
      this.hydrate();
    }

    hydrate() {
      this.renderAllDropdowns();
      this.wireFormChange();
      this.applyUrlStateToInputs();
      // Only take over the server-rendered grid if we actually loaded a
      // collection index. Without one, leave Shopify's grid + pagination
      // alone — the customer can still browse, just without our filters.
      if (this._state.hasIndex) {
        // ODA_LITE: skip the initial takeover on the default collection view —
        // leave Shopify's server grid + native order intact. wireFormChange()
        // (above) means the first filter/sort still triggers applyAndRender().
        if (!this.isDefaultView()) {
          this.applyAndRender();
        }
      } else {
        console.warn(
          '[waf] no collection index loaded (meta.collection_index_url is empty or fetch failed). ' +
          'Filter UI will render but filtering is disabled until publishCollectionIndexes runs.'
        );
        // Disable filter inputs visually so the customer doesn't waste
        // clicks. Keep the Subject dropdown (navigation) interactive.
        this.querySelectorAll('[data-waf-filter-input]').forEach((i) => {
          i.disabled = true;
          const label = i.closest('label');
          if (label) label.title = 'Filtering temporarily unavailable.';
        });
      }
      this.refreshSummary();
    }

    renderAllDropdowns() {
      const lite = isLiteTaxonomy(this._state.taxonomy);

      // ODA_LITE: only Koleksiyon (collections nav) + Color (renk swatches).
      const koleksiyonBody = this.querySelector('[data-waf-koleksiyon-menu]');
      if (koleksiyonBody) renderKoleksiyon(this._state, koleksiyonBody);

      const colorBody = this.querySelector('[data-waf-color-swatches]');
      if (colorBody) renderColorSwatches(this._state, colorBody);

      if (lite) return; // lite path has no subject/room/style/pieces dims.

      // Legacy (English darphane) 5-dim path — only runs if those
      // containers are present in the snippet (they're removed on lite).
      const subjectBody = this.querySelector('[data-waf-subject-menu]');
      const roomBody = this.querySelector('[data-waf-checklist]');
      const styleBody = this.querySelector('[data-waf-filter-tree]');
      if (subjectBody) renderSubject(this._state, subjectBody);
      if (roomBody) renderRoomChecklist(this._state, roomBody);
      if (styleBody) {
        renderStyleTree(this._state, styleBody);
        wireStyleTreeChevrons(styleBody);
      }
    }

    /** Push current URL state into checkbox `checked` values + expand
     *  any style branches that contain an active descendant + flip the
     *  visual `.is-active` class on the wrapping label so swatches show
     *  their checkmark and rooms/styles get the highlight background. */
    applyUrlStateToInputs() {
      this.querySelectorAll('[data-waf-filter-input]').forEach((input) => {
        if (input.name === 'filter.p.tag') {
          input.checked = this._urlState.tag.has(input.value);
        } else if (input.name === 'filter.p.product_type') {
          input.checked = this._urlState.productType.has(input.value);
        }
        const wrap = input.closest('.waf-swatch, .waf-check');
        if (wrap) wrap.classList.toggle('is-active', input.checked);
      });
      // Auto-expand any style branches that have a checked descendant.
      this.querySelectorAll('.waf-filter-tree .waf-tree-branch').forEach((branch) => {
        const hasChecked = branch.querySelector('[data-waf-filter-input]:checked');
        if (!hasChecked) return;
        let cur = branch.parentElement && branch.parentElement.closest('.waf-tree-branch');
        while (cur) {
          const kids = cur.querySelector(':scope > .waf-tree-children');
          const chev = cur.querySelector(':scope > .waf-tree-row > .waf-tree-chevron');
          if (kids) kids.hidden = false;
          if (chev) chev.setAttribute('aria-expanded', 'true');
          cur = cur.parentElement && cur.parentElement.closest('.waf-tree-branch');
        }
      });
    }

    wireFormChange() {
      this._form.addEventListener('change', this.onFormChange.bind(this));
    }

    onFormChange(ev) {
      const input = ev.target;
      // Cascade: if a style parent was toggled, set all descendants to
      // the same state BEFORE serializing.
      if (input && input.dataset && input.dataset.wafCascadeParent === '1') {
        const branch = input.closest('[data-waf-cascade-branch]');
        if (branch) {
          branch.querySelectorAll('[data-waf-filter-input]').forEach((other) => {
            if (other !== input) other.checked = input.checked;
          });
          // If we just checked the parent, expand its children so the
          // customer can see what cascaded.
          if (input.checked) {
            const kids = branch.querySelector(':scope > .waf-tree-children');
            const chev = branch.querySelector(':scope > .waf-tree-row > .waf-tree-chevron');
            if (kids) kids.hidden = false;
            if (chev) chev.setAttribute('aria-expanded', 'true');
          }
        }
      }
      // Serialize current form state → URL → re-render. Always reset to
      // page 1 because the filtered set just changed — staying on
      // (say) page 5 of the old result list makes no sense for the new
      // list.
      const newState = this.collectStateFromForm();
      newState.page = 1;
      writeUrlFilters(newState);
      this._urlState = newState;
      // If the customer just cleared their last filter (back to the bare
      // default collection view), hand the grid back to Shopify's native
      // server order rather than re-rendering our JS grid in index order.
      if (this.restoreNativeGridIfDefault()) return;
      // Re-sync visual state (.is-active class on swatches/checks +
      // expand-on-checked for style branches) before re-rendering.
      this.applyUrlStateToInputs();
      this.applyAndRender();
      this.refreshSummary();
    }

    collectStateFromForm() {
      const state = { tag: new Set(), productType: new Set(), sortBy: '', page: 1 };
      this.querySelectorAll('[data-waf-filter-input]:checked').forEach((i) => {
        if (i.name === 'filter.p.tag') state.tag.add(i.value);
        else if (i.name === 'filter.p.product_type') state.productType.add(i.value);
      });
      const mirror = this.querySelector('[data-waf-sort-mirror]');
      if (mirror && mirror.value) state.sortBy = mirror.value;
      return state;
    }

    applyAndRender() {
      const filtered = filterProducts(this._state.allProducts, this._urlState);
      const sorted = sortProducts(filtered, this._urlState.sortBy || 'featured');
      this._state.filtered = sorted;
      // Clamp current page to valid range (filter change may have shrunk
      // the result set below the previous page count).
      const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
      if (this._urlState.page > totalPages) {
        this._urlState.page = 1;
        writeUrlFilters(this._urlState);
      }
      // Fire-and-forget; renderGrid is async (image preload). Errors
      // are caught inside and surfaced via showLoadError.
      this.renderGrid();
    }

    async renderGrid() {
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      const page = this._urlState.page || 1;
      const start = (page - 1) * PAGE_SIZE;
      const end = Math.min(start + PAGE_SIZE, this._state.filtered.length);
      const slice = this._state.filtered.slice(start, end);

      // Empty result set — no images to preload, render straight away.
      if (slice.length === 0) {
        grid.innerHTML = '';
        const empty = document.createElement('li');
        empty.className = 'grid__item waf-grid-empty';
        empty.innerHTML = `<div class="waf-grid-empty__body"><h2>Bu filtrelere uygun ürün yok.</h2><p>Yukarıdaki bazı filtreleri temizlemeyi deneyin.</p></div>`;
        grid.appendChild(empty);
        this.renderPagination();
        return;
      }

      // Show spinner first so the customer sees a clear "loading" state
      // instead of a half-painted grid while images stream in. FORCED:
      // renderGrid only runs on an explicit filter / sort / page / search
      // (the default collection view never calls it — it keeps Shopify's
      // server grid), so a spinner here is always the right "working…"
      // feedback. Without force, the guard would keep the stale cards and
      // the filter would feel unresponsive until the new images finished.
      this.showLoadingSpinner(true);

      // Race guard: if a newer renderGrid() is triggered while we're
      // awaiting image preload (e.g. user clicks page 2 then page 3
      // rapidly), the OLDER render must abandon — otherwise it'd
      // overwrite the newer page's cards with the stale slice.
      this._renderGen = (this._renderGen || 0) + 1;
      const gen = this._renderGen;

      // Build cards into an off-DOM fragment so the IMG elements start
      // loading immediately (setting `src` triggers fetch regardless of
      // attachment to the live tree). When we later append the fragment
      // to #product-grid, the SAME IMG elements are reused — already
      // loaded → paint instantly, no flash of empty cards.
      const fragment = document.createDocumentFragment();
      const cards = [];
      for (const product of slice) {
        const card = buildCardEl(product);
        cards.push(card);
        fragment.appendChild(card);
      }

      await waitForImagesIn(cards);
      if (gen !== this._renderGen) return;

      grid.innerHTML = '';
      grid.appendChild(fragment);
      this.renderPagination();

      // Wire heart + more-like-this on the new cards. Both bind via
      // shopify:section:load — dispatched once, on a container so it
      // bubbles up to document where wishlist.js + wall-art-recommender.js
      // listen.
      const container = document.getElementById('ProductGridContainer');
      if (container) {
        container.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: this._state.meta && this._state.meta.section_id },
        }));
      }

      // Now that the current page is painted, idle-prefetch the
      // adjacent pages' images. By the time the customer clicks "next"
      // or "prev", all those images are already in the browser cache —
      // the spinner barely flashes before paint. Single src (no srcset)
      // means the prefetch URL matches the render URL exactly.
      this.prefetchAdjacentPages();
    }

    /** Background-prefetch images for the previous + next pages so
     *  pagination clicks hit a warm browser cache.
     *
     *  - Runs in requestIdleCallback so it doesn't compete with the
     *    current page's images for bandwidth or main-thread time.
     *  - Cancelled by the next renderGrid() via the _renderGen counter
     *    (if user rapidly clicks page 2 → 3 → 4, only page 4's adjacent
     *    pages get prefetched, not 2's or 3's).
     *  - Skips pages already prefetched in this session via a Set keyed
     *    by page number (resets when filters change since the slice
     *    differs).
     */
    prefetchAdjacentPages() {
      const totalPages = Math.ceil(this._state.filtered.length / PAGE_SIZE);
      const currentPage = this._urlState.page || 1;
      const targets = [];
      if (currentPage + 1 <= totalPages) targets.push(currentPage + 1);
      if (currentPage - 1 >= 1) targets.push(currentPage - 1);
      if (targets.length === 0) return;

      const gen = this._renderGen;
      const run = () => {
        if (gen !== this._renderGen) return; // superseded
        for (const pg of targets) {
          const start = (pg - 1) * PAGE_SIZE;
          const end = Math.min(start + PAGE_SIZE, this._state.filtered.length);
          for (let i = start; i < end; i += 1) {
            const p = this._state.filtered[i];
            if (!p || !p.img) continue;
            const pre = new Image();
            // Same URL the card's <img src> would use → guaranteed
            // cache hit on actual render.
            pre.src = resizedImageUrl(p.img, CARD_IMAGE_WIDTH);
          }
        }
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(run, { timeout: 3000 });
      } else {
        setTimeout(run, 200);
      }
    }

    /** Render numbered pagination matching Dawn's `pagination.liquid`
     *  markup so the existing `component-pagination.css` applies for
     *  free. Replaces the server-rendered .pagination-wrapper (which
     *  reflected the WHOLE collection's count, not the filtered set). */
    renderPagination() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;
      const totalPages = Math.ceil(this._state.filtered.length / PAGE_SIZE);
      let wrapper = container.querySelector('.pagination-wrapper');
      if (totalPages <= 1) {
        if (wrapper) wrapper.remove();
        return;
      }
      const current = this._urlState.page || 1;
      const parts = buildPaginationParts(current, totalPages);
      const itemHtml = parts.map((p) => {
        if (p === '…') return '<li><span class="pagination__item">…</span></li>';
        if (p === current) {
          return `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${p}">${p}</a></li>`;
        }
        return `<li><a href="${this._hrefForPage(p)}" class="pagination__item link" data-waf-page="${p}" aria-label="Page ${p}">${p}</a></li>`;
      }).join('');
      // Dawn's prev/next class naming is inverted: the "previous" link
      // gets `--next` (the caret rotates), the "next" link gets `--prev`.
      // Match Dawn so existing CSS applies.
      const prevHtml = current > 1
        ? `<li><a href="${this._hrefForPage(current - 1)}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-waf-page="${current - 1}" aria-label="Previous page"><span class="svg-wrapper">${CARET_SVG}</span></a></li>`
        : '';
      const nextHtml = current < totalPages
        ? `<li><a href="${this._hrefForPage(current + 1)}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-waf-page="${current + 1}" aria-label="Next page"><span class="svg-wrapper">${CARET_SVG}</span></a></li>`
        : '';
      const html = `<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">${prevHtml}${itemHtml}${nextHtml}</ul></nav>`;
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'pagination-wrapper';
        container.appendChild(wrapper);
      }
      wrapper.dataset.page = String(current);
      wrapper.innerHTML = html;
      wrapper.querySelectorAll('[data-waf-page]').forEach((a) => {
        a.addEventListener('click', (ev) => {
          ev.preventDefault();
          const p = parseInt(a.getAttribute('data-waf-page'), 10);
          if (!p) return;
          this._urlState.page = p;
          writeUrlFilters(this._urlState);
          this.renderGrid();
          // Scroll to top of the grid (not top of page — that'd hide the
          // filter bar the customer just used).
          const top = container.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    }

    /** Build the href for a given page number, preserving every other
     *  active query param. Used for the pagination links (right-click
     *  → "open in new tab" should work + give the same view). */
    _hrefForPage(p) {
      const url = new URL(window.location.href);
      if (p > 1) url.searchParams.set('page', String(p));
      else url.searchParams.delete('page');
      return url.pathname + (url.search ? url.search : '');
    }

    /** Update per-dim active counts in summaries + Clear All link
     *  visibility + match count + active-filter pills. */
    refreshSummary() {
      this.querySelectorAll('.waf-dropdown').forEach((d) => {
        const dim = d.dataset.dim;
        if (!dim) return;
        let count = 0;
        d.querySelectorAll('[data-waf-filter-input]:checked').forEach(() => { count += 1; });
        const c = d.querySelector('[data-waf-active-count]');
        if (c) c.textContent = count > 0 ? `(${count})` : '';
      });
      const anyActive = this._urlState.tag.size > 0 || this._urlState.productType.size > 0;
      const clearAll = this.querySelector('[data-waf-clear-all]');
      if (clearAll) clearAll.hidden = !anyActive;
      const matchCount = this.querySelector('[data-waf-match-count]');
      if (matchCount) {
        if (this._state.allProducts.length === 0) {
          matchCount.textContent = '';
        } else if (anyActive) {
          matchCount.textContent = `${this._state.allProducts.length.toLocaleString()} üründen ${this._state.filtered.length.toLocaleString()} tanesi`;
        } else {
          matchCount.textContent = `${this._state.allProducts.length.toLocaleString()} ürün`;
        }
      }
      this.renderActivePills();
    }

    /** Render a removable chip per active filter, below the bar.
     *  Clicking a chip's × removes that single filter (independent of
     *  the "Clear all" link which wipes everything). */
    renderActivePills() {
      const container = this.querySelector('[data-waf-pills]');
      if (!container) return;
      const pills = [];
      // Tag filters — resolve display label via taxonomy lookup.
      for (const tag of this._urlState.tag) {
        const idx = tag.indexOf(':');
        if (idx < 0) continue;
        const dim = tag.slice(0, idx);
        const handle = tag.slice(idx + 1);
        const node = getDimNode(this._state.taxonomy, dim, handle);
        const dimLabel = dim.charAt(0).toUpperCase() + dim.slice(1);
        const valueLabel = cleanTitle(node ? node.title : handle);
        pills.push({
          label: `${dimLabel}: ${valueLabel}`,
          type: 'tag',
          value: tag,
        });
      }
      // Pieces (product_type).
      for (const pt of this._urlState.productType) {
        pills.push({
          label: `Parça: ${pt === 'Wall Art Set' ? 'Duvar Seti' : 'Tek Parça'}`,
          type: 'productType',
          value: pt,
        });
      }
      if (pills.length === 0) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }
      container.hidden = false;
      container.innerHTML = pills.map((p) => `
        <button type="button" class="waf-pill" data-waf-pill-type="${escapeHtml(p.type)}" data-waf-pill-value="${escapeHtml(p.value)}" aria-label="${escapeHtml(p.label)} filtresini kaldır">
          <span class="waf-pill__label">${escapeHtml(p.label)}</span>
          <span class="waf-pill__x" aria-hidden="true">&times;</span>
        </button>
      `).join('');
      container.querySelectorAll('.waf-pill').forEach((btn) => {
        btn.addEventListener('click', () => {
          const type = btn.getAttribute('data-waf-pill-type');
          const value = btn.getAttribute('data-waf-pill-value');
          if (type === 'tag') this._urlState.tag.delete(value);
          else if (type === 'productType') this._urlState.productType.delete(value);
          this._urlState.page = 1;
          writeUrlFilters(this._urlState);
          // Removing the last pill → back to the default view → restore
          // Shopify's native server grid order (see restoreNativeGridIfDefault).
          if (this.restoreNativeGridIfDefault()) return;
          this.applyUrlStateToInputs();
          this.applyAndRender();
          this.refreshSummary();
        });
      });
    }
  }
  if (!customElements.get('waf-filter-form')) {
    customElements.define('waf-filter-form', WafFilterForm);
  }

  /* ─────────────────────────────────────────────────────────────────
   * Dropdown UX (close-on-outside, close-others, ESC)
   * ──────────────────────────────────────────────────────────────── */

  function setupDropdownBehavior(scope) {
    const dropdowns = scope.querySelectorAll('[data-waf-dropdown]');
    dropdowns.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (!d.open) return;
        dropdowns.forEach((other) => {
          if (other !== d && other.open) other.open = false;
        });
      });
    });
    document.addEventListener('mousedown', (ev) => {
      const t = ev.target;
      if (!(t instanceof Node)) return;
      let inside = false;
      dropdowns.forEach((d) => { if (d.contains(t)) inside = true; });
      if (!inside) dropdowns.forEach((d) => { if (d.open) d.open = false; });
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') dropdowns.forEach((d) => { if (d.open) d.open = false; });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * popstate (back/forward within same document) — re-apply URL state.
   * Fires when the user navigates via the pushState history we wrote
   * in writeUrlFilters (e.g. back from page 2 → page 1).
   * ──────────────────────────────────────────────────────────────── */

  function reapplyFromCurrentUrl(reason) {
    const ff = document.querySelector('waf-filter-form');
    if (!ff || !ff._initialized) return;
    // Reset the render-generation counter so any in-flight render that
    // was mid-image-preload when the user navigated away is treated as
    // superseded and won't accidentally overwrite the fresh render
    // below. Otherwise: stale slice + broken IMGs (network aborted
    // during navigation away) end up committed and the grid looks dead.
    ff._renderGen = (ff._renderGen || 0) + 1;
    ff._urlState = readUrlFilters();
    ff.applyUrlStateToInputs();
    ff.applyAndRender();
    ff.refreshSummary();

    // Clear stale "loading" visual state that can survive a navigation
    // even though our own JS doesn't add it. Concretely:
    //   - Dawn's `.collection.loading` toggles a 0.7-opacity overlay
    //     (`.loading-overlay`) sitting on top of the grid via CSS
    //     `template-collection.css`. If anything (Dawn's facets.js,
    //     pagination AJAX) added this class and the navigation away
    //     killed the response, the class never gets removed → cards
    //     look permanently faded after bfcache restore.
    //   - The scroll-trigger animation on `#ProductGridContainer`
    //     (`scroll-trigger animate--slide-in`) starts at opacity 0.01
    //     and animates to 1 with `forwards`. On bfcache restore the
    //     animation state can be reset by some browsers; force it to
    //     the final state by removing the trigger class entirely.
    document.querySelectorAll('.collection.loading').forEach((el) => {
      el.classList.remove('loading');
    });
    const grid = document.getElementById('ProductGridContainer');
    if (grid) {
      // Strip the slide-in animation class so the base opacity 0.01
      // rule no longer applies. Animation already played on initial
      // load — it's not meant to replay on every back-nav anyway.
      grid.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      grid.style.opacity = '';
    }

    if (reason) console.debug('[waf] reapplied URL state on', reason);
  }

  window.addEventListener('popstate', () => reapplyFromCurrentUrl('popstate'));

  /* ─────────────────────────────────────────────────────────────────
   * pageshow (event.persisted=true) — bfcache restore.
   *
   * When the customer clicks a product card, then hits browser-back,
   * the browser may restore the collection page from the back/forward
   * cache instead of re-running the document. In that case:
   *   - DOMContentLoaded does NOT fire (page was never torn down)
   *   - popstate does NOT fire (URL unchanged from when we navigated away)
   *   - In-flight image-preload promises may still be pending OR
   *     resolved-but-cancelled, leaving the grid in whatever half-state
   *     it was in (often: spinner stuck, or cards with broken IMGs).
   *
   * Forcing a fresh render via the same path as popstate fixes both
   * scenarios uniformly. Skipped for normal first loads (persisted=false)
   * — those go through connectedCallback as usual.
   * ──────────────────────────────────────────────────────────────── */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reapplyFromCurrentUrl('pageshow:bfcache');
  });
})();
